import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { API, useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";

export default function Admin() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([
        fetch(`${API}/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`, {
          credentials: "include",
        }),
        fetch(`${API}/admin/stats`, { credentials: "include" }),
      ]);
      if (!u.ok) throw new Error("admin");
      setUsers(await u.json());
      if (s.ok) setStats(await s.json());
    } catch (e) {
      toast.error("Forbidden or error");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (uid, body, label) => {
    try {
      const r = await fetch(`${API}/admin/users/${uid}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("patch");
      toast.success(label);
      load();
    } catch (e) {
      toast.error("Error");
    }
  };

  const resetQuota = async (uid) => {
    try {
      const r = await fetch(`${API}/admin/users/${uid}/reset-quota`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error();
      toast.success("Quota reset");
      load();
    } catch {
      toast.error("Error");
    }
  };

  if (user && !user.is_admin) return <Navigate to="/app" replace />;

  return (
    <main className="flex-grow" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#FF3B30] font-bold mb-2">
          // RESTRICTED ZONE
        </p>
        <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight mb-6">
          {t("admin_title")}
        </h1>

        {stats && (
          <div
            data-testid="admin-stats"
            className="mb-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
          >
            {[
              { l: "USERS", v: stats.total_users, sub: `${stats.plan_counts.business}B · ${stats.plan_counts.pro}P · ${stats.plan_counts.free}F` },
              { l: "TRADUCTIONS", v: stats.total_translations.toLocaleString() },
              { l: "REVENU", v: `${stats.total_revenue_eur.toFixed(2)}€`, sub: `${stats.paid_txns} txns` },
              { l: "COÛT IA", v: `${stats.total_cost_eur.toFixed(2)}€`, sub: `${stats.cost_per_translation_eur.toFixed(3)}€/trad` },
              { l: "MARGE", v: `${stats.total_margin_eur.toFixed(2)}€`, accent: stats.total_margin_eur >= 0 },
              { l: "ARPU", v: `${stats.arpu_eur.toFixed(2)}€`, sub: `coût/user ${stats.avg_cost_per_user_eur.toFixed(3)}€` },
            ].map((s) => (
              <div
                key={s.l}
                className={`border-2 border-[#0A0A0A] p-3 ${s.accent === false ? "bg-[#FF3B30] text-white" : s.accent === true ? "bg-[#FFCC00]" : "bg-white"}`}
              >
                <div className="text-[10px] tracking-[0.2em] font-bold uppercase opacity-70">{s.l}</div>
                <div className="font-display text-2xl mt-1">{s.v}</div>
                {s.sub && <div className="text-[10px] uppercase mt-1 opacity-70">{s.sub}</div>}
              </div>
            ))}
          </div>
        )}

        <div className="mb-6 flex flex-col sm:flex-row gap-3">
          <input
            data-testid="admin-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("admin_search")}
            className="bg-white border-2 border-[#0A0A0A] px-3 py-2 font-mono-corp text-sm focus:outline-none focus:bg-[#FFCC00] flex-grow"
          />
          <button
            data-testid="admin-refresh-btn"
            onClick={load}
            className="bg-white border-2 border-[#0A0A0A] font-display text-xs uppercase tracking-widest px-4 py-2 shadow-brutal-sm hover:bg-[#FFCC00] transition-all"
          >
            REFRESH
          </button>
        </div>

        <div className="bg-white border-2 border-[#0A0A0A] shadow-brutal overflow-x-auto">
          <table className="w-full border-collapse">
            <thead className="bg-[#0A0A0A] text-[#F4F4F0]">
              <tr>
                {[
                  t("admin_col_email"),
                  t("admin_col_plan"),
                  t("admin_col_status"),
                  "TRAD.",
                  "REVENU",
                  "COÛT",
                  "MARGE",
                  t("admin_col_actions"),
                ].map((h) => (
                  <th
                    key={h}
                    className="text-left text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-3 border-r border-[#F4F4F0] last:border-r-0"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="font-mono-corp text-xs">
              {loading && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center">
                    <span className="blink">▮</span> LOADING...
                  </td>
                </tr>
              )}
              {!loading && users.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-[#4B4B4B]">
                    No users found.
                  </td>
                </tr>
              )}
              {users.map((u) => (
                <tr
                  key={u.user_id}
                  data-testid={`admin-row-${u.user_id}`}
                  className="border-t-2 border-[#0A0A0A] hover:bg-[#F4F4F0]"
                >
                  <td className="px-3 py-3 break-all">
                    {u.email}
                    {u.is_admin && (
                      <span className="ml-2 inline-block bg-[#FFCC00] border border-[#0A0A0A] px-1 text-[10px] font-bold">
                        ADMIN
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`inline-block px-2 py-1 text-[10px] font-bold uppercase border-2 border-[#0A0A0A] ${
                        u.plan === "business"
                          ? "bg-[#FFCC00]"
                          : u.plan === "pro"
                          ? "bg-[#F4F4F0]"
                          : "bg-white"
                      }`}
                    >
                      {u.plan}
                    </span>
                    {u.pro_until && (
                      <div className="text-[10px] mt-1 text-[#4B4B4B]">
                        →{" "}
                        {new Date(u.pro_until).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {u.is_blocked ? (
                      <span className="bg-[#FF3B30] text-white px-2 py-1 text-[10px] font-bold uppercase border-2 border-[#0A0A0A]">
                        {t("admin_blocked")}
                      </span>
                    ) : (
                      <span className="text-[#4B4B4B] text-[10px] font-bold uppercase">
                        {t("admin_active")}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {u.lifetime_translations ?? 0}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap font-bold">
                    {(u.total_paid_eur ?? 0).toFixed(2)}€
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-[#4B4B4B]">
                    {(u.estimated_cost_eur ?? 0).toFixed(3)}€
                  </td>
                  <td
                    data-testid={`admin-margin-${u.user_id}`}
                    className={`px-3 py-3 whitespace-nowrap font-bold ${(u.margin_eur ?? 0) >= 0 ? "text-[#0A0A0A]" : "text-[#FF3B30]"}`}
                  >
                    {(u.margin_eur ?? 0).toFixed(2)}€
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-2">
                      {u.plan !== "pro" && (
                        <button
                          data-testid={`btn-make-pro-${u.user_id}`}
                          onClick={() =>
                            patch(u.user_id, { plan: "pro" }, "Upgraded to Pro")
                          }
                          className="bg-[#F4F4F0] border-2 border-[#0A0A0A] text-[10px] font-bold uppercase tracking-widest px-2 py-1 hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
                        >
                          → PRO
                        </button>
                      )}
                      {u.plan !== "business" && (
                        <button
                          data-testid={`btn-make-business-${u.user_id}`}
                          onClick={() =>
                            patch(u.user_id, { plan: "business" }, "Upgraded to Business")
                          }
                          className="bg-[#FFCC00] border-2 border-[#0A0A0A] text-[10px] font-bold uppercase tracking-widest px-2 py-1 hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
                        >
                          → BUSINESS
                        </button>
                      )}
                      {u.plan !== "free" && (
                        <button
                          data-testid={`btn-make-free-${u.user_id}`}
                          onClick={() =>
                            patch(u.user_id, { plan: "free" }, "Downgraded to Free")
                          }
                          className="bg-white border-2 border-[#0A0A0A] text-[10px] font-bold uppercase tracking-widest px-2 py-1"
                        >
                          {t("admin_make_free")}
                        </button>
                      )}
                      {u.is_blocked ? (
                        <button
                          data-testid={`btn-unblock-${u.user_id}`}
                          onClick={() =>
                            patch(u.user_id, { is_blocked: false }, "Unblocked")
                          }
                          className="bg-white border-2 border-[#0A0A0A] text-[10px] font-bold uppercase tracking-widest px-2 py-1"
                        >
                          {t("admin_unblock")}
                        </button>
                      ) : (
                        <button
                          data-testid={`btn-block-${u.user_id}`}
                          onClick={() =>
                            patch(u.user_id, { is_blocked: true }, "Blocked")
                          }
                          className="bg-[#FF3B30] text-white border-2 border-[#0A0A0A] text-[10px] font-bold uppercase tracking-widest px-2 py-1"
                        >
                          {t("admin_block")}
                        </button>
                      )}
                      <button
                        data-testid={`btn-reset-quota-${u.user_id}`}
                        onClick={() => resetQuota(u.user_id)}
                        className="bg-white border-2 border-[#0A0A0A] text-[10px] font-bold uppercase tracking-widest px-2 py-1"
                      >
                        {t("admin_reset_quota")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}
